import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Video, Square, Pause, Play, Download, X, Camera, Circle, Mic, MonitorPlay,
} from 'lucide-react';
import { toast } from 'sonner';

const MIME_CANDIDATES = [
  'video/mp4;codecs=avc1',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

const pickMime = () => MIME_CANDIDATES.find(
  (m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

/**
 * In-app screen recorder: getDisplayMedia + MediaRecorder, nothing leaves the
 * browser. Records screen/tab/window with optional microphone and tab audio,
 * supports pause/resume, grabs a still frame, and hands back a downloadable file.
 */
export default function ScreenRecorder() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle');       // idle | recording | paused | done
  const [seconds, setSeconds] = useState(0);
  const [withMic, setWithMic] = useState(true);
  const [withTabAudio, setWithTabAudio] = useState(true);
  const [result, setResult] = useState(null);        // { url, size, ext }
  const [shot, setShot] = useState(null);            // still frame data URL
  const [blocked, setBlocked] = useState(false);
  const [wide, setWide] = useState(
    typeof window === 'undefined' ? true : window.innerWidth >= 640);

  const recorder = useRef(null);
  const chunks = useRef([]);
  const streams = useRef([]);
  const videoTrack = useRef(null);
  const timer = useRef(null);

  const supported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getDisplayMedia && !!window.MediaRecorder;

  const cleanup = useCallback(() => {
    clearInterval(timer.current);
    streams.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    streams.current = [];
    videoTrack.current = null;
    recorder.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const start = async () => {
    setResult(null);
    setShot(null);
    setBlocked(false);
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: withTabAudio,
      });
      streams.current.push(display);
      videoTrack.current = display.getVideoTracks()[0];

      const tracks = [...display.getVideoTracks(), ...display.getAudioTracks()];
      if (withMic) {
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
          streams.current.push(mic);
          tracks.push(...mic.getAudioTracks());
        } catch (e) {
          toast.warning('Microphone was refused — recording without narration');
        }
      }

      const mixed = new MediaStream(tracks);
      const mimeType = pickMime();
      const rec = new MediaRecorder(mixed, mimeType ? { mimeType } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || 'video/webm';
        const blob = new Blob(chunks.current, { type });
        setResult({
          url: URL.createObjectURL(blob),
          size: blob.size,
          ext: type.includes('mp4') ? 'mp4' : 'webm',
        });
        setState('done');
        cleanup();
      };
      // the browser's own "Stop sharing" button
      videoTrack.current.addEventListener('ended', () => {
        if (rec.state !== 'inactive') rec.stop();
      });

      rec.start(1000);
      recorder.current = rec;
      setState('recording');
      setSeconds(0);
      clearInterval(timer.current);
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      toast.success('Recording — your screen never leaves this browser');
    } catch (e) {
      cleanup();
      // Anything other than the user simply cancelling means capture is blocked
      // (iframes reject with NotAllowedError, SecurityError, TypeError or
      // NotSupportedError depending on the browser).
      const denied = e?.name !== 'AbortError';
      setBlocked(denied);
      toast.error(denied
        ? 'Screen capture was blocked. In the preview frame browsers disallow it — open the app in its own tab.'
        : e.message || 'Could not start recording');
    }
  };

  const pause = () => {
    if (recorder.current?.state === 'recording') {
      recorder.current.pause();
      clearInterval(timer.current);
      setState('paused');
    }
  };

  const resume = () => {
    if (recorder.current?.state === 'paused') {
      recorder.current.resume();
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setState('recording');
    }
  };

  const stop = () => {
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
    clearInterval(timer.current);
  };

  const snapshot = async () => {
    const track = videoTrack.current;
    if (!track) return;
    try {
      const video = document.createElement('video');
      video.srcObject = new MediaStream([track]);
      video.muted = true;
      await video.play();
      await new Promise((r) => setTimeout(r, 200));
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      setShot(canvas.toDataURL('image/png'));
      video.pause();
      toast.success('Frame captured — download it below');
    } catch (e) {
      toast.error('Could not capture a frame');
    }
  };

  const download = (href, name) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.click();
  };

  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  const recording = state === 'recording' || state === 'paused';

  // Mobile browsers cannot capture the screen at all — render nothing there.
  if (!supported || !wide) return null;

  return (
    <>
      {/* Floating trigger */}
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-4 left-4 z-[70] h-11 px-4 rounded-full shadow-lg text-[13px] font-medium
          inline-flex items-center gap-2 transition-colors ${recording
            ? 'bg-rose-600 hover:bg-rose-700 text-white'
            : 'bg-[#0b1a35] hover:bg-[#1d4ed8] text-white'}`}
        title="Record your screen" data-testid="screen-recorder-fab">
        {recording
          ? <><Circle className="w-3 h-3 fill-current animate-pulse" /> {fmt(seconds)}</>
          : <><Video className="w-4 h-4" /> Record</>}
      </button>

      {open && (
        <div className="fixed bottom-20 left-4 z-[70] w-[340px] max-w-[calc(100vw-2rem)] rounded-xl
          border border-border bg-background/98 backdrop-blur shadow-2xl p-4 space-y-3"
          data-testid="screen-recorder-panel">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-medium inline-flex items-center gap-1.5">
              <MonitorPlay className="w-4 h-4 text-[#1d4ed8]" /> Screen recorder
            </span>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setOpen(false)}
              data-testid="screen-recorder-close"><X className="w-4 h-4" /></Button>
          </div>

          {!supported && (
            <p className="text-[12px] text-muted-foreground" data-testid="screen-recorder-unsupported">
              This browser cannot capture the screen. Use a recent Chrome, Edge or Firefox on desktop.
            </p>
          )}

          {supported && state === 'idle' && (
            <>
              <p className="text-[12px] text-muted-foreground">
                Records the screen, window or tab you pick. Everything stays in your browser —
                nothing is uploaded.
              </p>
              <label className="flex items-center gap-2 text-[12px]">
                <input type="checkbox" checked={withMic} className="accent-[#1d4ed8]"
                  onChange={(e) => setWithMic(e.target.checked)}
                  data-testid="screen-recorder-mic-toggle" />
                <Mic className="w-3.5 h-3.5" /> Record my microphone (narration)
              </label>
              <label className="flex items-center gap-2 text-[12px]">
                <input type="checkbox" checked={withTabAudio} className="accent-[#1d4ed8]"
                  onChange={(e) => setWithTabAudio(e.target.checked)}
                  data-testid="screen-recorder-tabaudio-toggle" />
                Include the audio of the shared tab
              </label>
              <Button onClick={start} className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="screen-recorder-start">
                <Video className="w-4 h-4 mr-2" /> Start recording
              </Button>
              {blocked && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400"
                  data-testid="screen-recorder-blocked">
                  Blocked in this frame. <a className="underline" href={window.location.href}
                    target="_blank" rel="noreferrer">Open the app in its own tab</a> and try again.
                </p>
              )}
            </>
          )}

          {supported && recording && (
            <>
              <div className="flex items-center gap-2 text-[13px]">
                <Circle className={`w-3 h-3 fill-current text-rose-600 ${state === 'recording' ? 'animate-pulse' : ''}`} />
                <span className="tabular-nums font-medium" data-testid="screen-recorder-timer">{fmt(seconds)}</span>
                <span className="text-muted-foreground text-[12px]">
                  {state === 'paused' ? 'paused' : 'recording'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {state === 'recording' ? (
                  <Button size="sm" variant="outline" onClick={pause} data-testid="screen-recorder-pause">
                    <Pause className="w-3.5 h-3.5 mr-1" /> Pause
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={resume} data-testid="screen-recorder-resume">
                    <Play className="w-3.5 h-3.5 mr-1" /> Resume
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={snapshot} data-testid="screen-recorder-snapshot">
                  <Camera className="w-3.5 h-3.5 mr-1" /> Frame
                </Button>
                <Button size="sm" onClick={stop} className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="screen-recorder-stop">
                  <Square className="w-3.5 h-3.5 mr-1" /> Stop
                </Button>
              </div>
            </>
          )}

          {state === 'done' && result && (
            <div className="space-y-2" data-testid="screen-recorder-result">
              <video src={result.url} controls className="w-full rounded border border-border"
                data-testid="screen-recorder-preview" />
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{fmt(seconds)} · {(result.size / 1048576).toFixed(1)} MB · .{result.ext}</span>
                <button type="button" className="underline" onClick={() => { setState('idle'); setResult(null); }}
                  data-testid="screen-recorder-again">record again</button>
              </div>
              <Button onClick={() => download(result.url, `inkphd-recording-${stamp}.${result.ext}`)}
                className="w-full" data-testid="screen-recorder-download">
                <Download className="w-4 h-4 mr-2" /> Download the recording
              </Button>
            </div>
          )}

          {shot && (
            <div className="space-y-1" data-testid="screen-recorder-shot">
              <img src={shot} alt="captured frame" className="w-full rounded border border-border" />
              <Button size="sm" variant="outline" className="w-full"
                onClick={() => download(shot, `inkphd-frame-${stamp}.png`)}
                data-testid="screen-recorder-shot-download">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download this frame (PNG)
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
